import {Button, Spin} from 'antd';
import React from 'react';
import {useHistory} from 'react-router-dom';

import {ListArrayFactory} from 'components/@common/specific/list-array-factory';
import {SignatureActionButton} from 'components/ais/reclaiming-information-card/fields/signature-action-button';
import {SignatureItem} from 'components/ais/reclaiming-information-card/fields/signature-item/signature-item';
import {useUploadSignature} from 'components/ais/reclaiming-information-card/hooks';
import {DateListItem} from 'components/form/fields/date-list-item';
import {DownloadDocumentListItem} from 'components/form/fields/download-document-list-item';
import {DATE_DMY} from 'shared/constants/date-format';
import {URL_AIS3_DOCUMENT_INVENTORY} from 'shared/constants/urls';
import {useEntityFromLocation} from 'shared/hooks/use-entity-from-location';
import {useUserRoleFunctions} from 'shared/hooks/use-user-role-functions';
import {showMessage} from 'shared/utils';
import {
    TReclaimingInformationEntity,
    useLazyGetReclaimingInformationQuery,
    useRunSubmitProtocolServiceMutation,
    useRunSubmitInabilityInfoServiceMutation,
    useRunSubmitResponseToDocRequestServiceMutation,
    EExternalServiceNames,
} from 'store/api/reclaiming-information/reclaiming-information.api';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

interface IReclaimingMainInfoProps {
    id: string;
    initialLoadedData: TReclaimingInformationEntity;
    onClose: () => void;
}

export const ReclaimingMainInfo = ({id, initialLoadedData, onClose}: IReclaimingMainInfoProps) => {
    const history = useHistory();
    const {entityName: entityNameFromLocation} = useEntityFromLocation();

    const entityName = entityNameFromLocation ?? 'fileUpload';

    const [runSubmitProtocolService, {isLoading: isSubmitProtocolLoading}] = useRunSubmitProtocolServiceMutation();
    const [
        runSubmitInabilityInfoService, {isLoading: isSubmitInabilityInfoLoading},
    ] = useRunSubmitInabilityInfoServiceMutation();
    const [
        runSubmitResponseToDocRequestService, {isLoading: isSubmitResponseToDocRequestLoading},
    ] = useRunSubmitResponseToDocRequestServiceMutation();

    const {hasUserRoleFunction} = useUserRoleFunctions({sections: [entityName]});
    const isDownloadAllowed = hasUserRoleFunction(EUserRoleFunction.DOWNLOAD);

    const [
        getReclaimingInformationQuery,
        {
            currentData,
            isFetching: isDataRefetching,
        },
    ] = useLazyGetReclaimingInformationQuery();

    const reloadData = () => {
        getReclaimingInformationQuery({id});
    };

    const reclaimingInfo = currentData ?? initialLoadedData;

    const [isProtocolSignatureUploading, handleUploadProtocolSignature] = useUploadSignature({
        attachmentId: reclaimingInfo?.protocolMainDocument?.attachmentId,
        reloadData,
    });
    const [isInabilityInfoSignatureUploading, handleUploadInabilityInfoSignature] = useUploadSignature({
        attachmentId: reclaimingInfo?.notificationDocument?.attachmentId?.toString(),
        reloadData,
    });
    const [isResponseToDocSignatureUploading, handleUploadResponseToDocSignature] = useUploadSignature({
        attachmentId: reclaimingInfo?.responseRequirementDocument?.attachmentId,
        reloadData,
    });

    const handleCreateInventoryClick = () => {
        const docIdParam = `?docId=${reclaimingInfo?.fileListMainDocument?.[0]?.objectId}`;
        history.push(`${URL_AIS3_DOCUMENT_INVENTORY}${docIdParam}`);
    };

    const renderFormationButton = () => (
        <Button
            type="primary"
            onClick={handleCreateInventoryClick}
        >
            Сформировать
        </Button>
    );

    return (
        <Spin
            spinning={isDataRefetching}
            tip="Подпись загружена, обновляем данные..."
        >
            <ListArrayFactory
                listArrayDescription={[
                    {
                        listTitle: 'Основная информация о требовании',
                        list: [
                            {
                                title: 'Номер требования:',
                                description: reclaimingInfo?.mainDocNumber || '-',
                            },
                            {
                                title: 'Дата получения:',
                                description: <DateListItem
                                    date={reclaimingInfo?.downloadDate}
                                />,
                            },
                            {
                                title: 'Файл требования:',
                                description: <DownloadDocumentListItem
                                    entityName={entityName}
                                    isDownloadDisabled={!isDownloadAllowed}
                                    fileList={
                                        reclaimingInfo?.fileListMainDocument?.[0]
                                            ? [reclaimingInfo?.fileListMainDocument?.[0]]
                                            : undefined
                                    }
                                />,
                            },
                            {
                                title: 'Подпись:',
                                description: <SignatureItem
                                    signatureName={reclaimingInfo?.fileListMainDocument?.[0]?.signatureFileName}
                                    attachmentId={reclaimingInfo?.fileListMainDocument?.[0].attachmentId}
                                    entityName={entityName}
                                    isDownloadDisabled={!isDownloadAllowed}
                                />,
                            },
                            {
                                title: 'Файл поручения:',
                                description: <DownloadDocumentListItem
                                    fileList={
                                        reclaimingInfo?.fileListMainDocument?.[1]
                                            ? [reclaimingInfo?.fileListMainDocument?.[1]]
                                            : undefined
                                    }
                                    entityName={entityName}
                                    isDownloadDisabled={!isDownloadAllowed}
                                />,
                            },
                            {
                                title: 'Подпись поручения:',
                                description: <SignatureItem
                                    signatureName={reclaimingInfo?.fileListMainDocument?.[1]?.signatureFileName}
                                    attachmentId={reclaimingInfo?.fileListMainDocument?.[1].attachmentId}
                                    entityName={entityName}
                                    isDownloadDisabled={!isDownloadAllowed}
                                />,
                            },
                        ],
                    },
                    {
                        listTitle: 'Протокол обработки требования',
                        list: [{
                            title: 'Файл протокола:',
                            description: <DownloadDocumentListItem
                                fileList={
                                    reclaimingInfo?.protocolMainDocument
                                        ? [reclaimingInfo?.protocolMainDocument] : []
                                }
                                entityName={entityName}
                                isDownloadDisabled={!isDownloadAllowed}
                            />,
                        },
                        {
                            title: 'Подпись:',
                            description: <SignatureItem
                                signatureName={reclaimingInfo?.protocolMainDocument?.signatureFileName}
                                attachmentId={reclaimingInfo?.protocolMainDocument?.attachmentId}
                                entityName={entityName}
                                isDownloadDisabled={!isDownloadAllowed}
                            />,
                        },
                        {
                            title: 'Дата отправки:',
                            description: <DateListItem
                                date={reclaimingInfo?.processingAudit?.createdDate}
                            />,
                        },
                        ],
                        useButtonsBottomMargin: true,
                        buttons: [
                            <SignatureActionButton
                                signatureName={reclaimingInfo?.protocolMainDocument?.signatureFileName}
                                attachmentId={reclaimingInfo?.protocolMainDocument?.attachmentId}
                                attachmentFileName={reclaimingInfo?.protocolMainDocument?.fileName}
                                isSignatureUploading={isProtocolSignatureUploading}
                                handleUploadFile={handleUploadProtocolSignature}
                            />,
                            <Button
                                type="primary"
                                disabled={
                                    !reclaimingInfo?.protocolMainDocument?.attachmentId
                                        || !reclaimingInfo?.protocolMainDocument?.signatureFileName
                                }
                                onClick={() => {
                                    runSubmitProtocolService({
                                        mainDocNumber: reclaimingInfo?.mainDocNumber,
                                        externalServiceName: EExternalServiceNames.SUBMIT_PROTOCOL,
                                    })
                                        .unwrap()
                                        .then(response => {
                                            showMessage({message: response});
                                            onClose();
                                        });
                                }}
                                loading={isSubmitProtocolLoading}
                            >
                                Отправить
                            </Button>,
                        ],
                    },
                    {
                        listTitle: 'Ответ на требование',
                        list: [{
                            title: 'Файл ответа на требование:',
                            description: <DownloadDocumentListItem
                                fileList={
                                    reclaimingInfo?.responseRequirementDocument
                                        ? [reclaimingInfo?.responseRequirementDocument] : []
                                }
                                entityName={entityName}
                                isDownloadDisabled={!isDownloadAllowed}
                            />,
                        },
                        {
                            title: 'Подпись:',
                            description: <SignatureItem
                                signatureName={reclaimingInfo?.responseRequirementDocument?.signatureFileName}
                                attachmentId={reclaimingInfo?.responseRequirementDocument?.attachmentId}
                                entityName={entityName}
                                isDownloadDisabled={!isDownloadAllowed}
                            />,
                        },
                        {
                            title: 'Дата отправки:',
                            description: <DateListItem
                                date={reclaimingInfo?.responseRequirementAudit?.createdDate}
                            />,
                        },
                        ],
                        useButtonsBottomMargin: true,
                        buttons: [
                            renderFormationButton(),
                            <SignatureActionButton
                                signatureName={reclaimingInfo?.responseRequirementDocument?.signatureFileName}
                                attachmentId={reclaimingInfo?.responseRequirementDocument?.attachmentId}
                                attachmentFileName={reclaimingInfo?.responseRequirementDocument?.fileName}
                                isSignatureUploading={isResponseToDocSignatureUploading}
                                handleUploadFile={handleUploadResponseToDocSignature}
                            />,
                            <Button
                                type="primary"
                                disabled={
                                    !reclaimingInfo?.responseRequirementDocument?.attachmentId
                                    || !reclaimingInfo?.responseRequirementDocument?.signatureFileName
                                }
                                onClick={() => {
                                    runSubmitResponseToDocRequestService({
                                        mainDocNumber: reclaimingInfo?.mainDocNumber,
                                        externalServiceName: EExternalServiceNames.SUBMIT_RESPONSE_TO_DOC_REQUEST,
                                    })
                                        .unwrap()
                                        .then(response => {
                                            showMessage({message: response});
                                            onClose();
                                        });
                                }}
                                loading={isSubmitResponseToDocRequestLoading}
                            >
                                Отправить
                            </Button>,
                        ],
                    },
                    {
                        listTitle: 'Протокол обработки ответа на требование',
                        list: [{
                            title: 'Файл протокола:',
                            description: <DownloadDocumentListItem
                                fileList={
                                    reclaimingInfo?.protocolResponseRequirementDocument
                                        ? [reclaimingInfo?.protocolResponseRequirementDocument] : []
                                }
                                entityName={entityName}
                                isDownloadDisabled={!isDownloadAllowed}
                            />,
                        },
                        {
                            title: 'Подпись:',
                            description: <SignatureItem
                                signatureName={
                                    reclaimingInfo?.protocolResponseRequirementDocument?.signatureFileName
                                }
                                attachmentId={reclaimingInfo?.protocolResponseRequirementDocument?.attachmentId}
                                entityName={entityName}
                                isDownloadDisabled={!isDownloadAllowed}
                            />,
                        },
                        {
                            title: 'Дата получения:',
                            description: <DateListItem
                                date={reclaimingInfo?.incomingProcessingAudit?.createdDate}
                            />,
                        },
                        ],
                        useButtonsBottomMargin: true,
                    },
                    {
                        listTitle: 'Уведомление о невозможности предоставить документ в срок',
                        list: [{
                            title: 'Файл уведомления:',
                            description: <DownloadDocumentListItem
                                fileList={
                                    reclaimingInfo?.notificationDocument
                                        ? [reclaimingInfo?.notificationDocument] : []
                                }
                                entityName={entityName}
                                isDownloadDisabled={!isDownloadAllowed}
                            />,
                        },
                        {
                            title: 'Подпись:',
                            description: <SignatureItem
                                signatureName={reclaimingInfo?.notificationDocument?.signatureFileName}
                                attachmentId={reclaimingInfo?.notificationDocument?.attachmentId}
                                entityName={entityName}
                                isDownloadDisabled={!isDownloadAllowed}
                            />,
                        },
                        {
                            title: 'Дата отправки:',
                            description: <DateListItem
                                date={reclaimingInfo?.notificationAudit?.createdDate}
                            />,
                        },
                        ],
                        useButtonsBottomMargin: true,
                        buttons: [
                            renderFormationButton(),
                            <SignatureActionButton
                                signatureName={
                                    reclaimingInfo?.notificationDocument?.signatureFileName
                                }
                                attachmentId={reclaimingInfo?.notificationDocument?.attachmentId}
                                attachmentFileName={reclaimingInfo?.notificationDocument?.fileName}
                                isSignatureUploading={isInabilityInfoSignatureUploading}
                                handleUploadFile={handleUploadInabilityInfoSignature}
                            />,
                            <Button
                                type="primary"
                                disabled={
                                    !reclaimingInfo?.notificationDocument?.attachmentId
                                    || !reclaimingInfo?.notificationDocument?.signatureFileName
                                }
                                onClick={() => {
                                    const objectId = reclaimingInfo?.notificationDocument?.objectId;
                                    if (!objectId) {
                                        return;
                                    }
                                    runSubmitInabilityInfoService({id: objectId?.toString()})
                                        .unwrap()
                                        .then(onClose);
                                }}
                                loading={isSubmitInabilityInfoLoading}
                            >
                                Отправить
                            </Button>,
                        ],
                    },
                    {
                        listTitle: 'Решение о продлении сроков предоставления документов',
                        list: [{
                            title: 'Файл решения в xml:',
                            description: <DownloadDocumentListItem
                                fileList={
                                    reclaimingInfo?.solutionDocument?.[0]
                                        ? [reclaimingInfo?.solutionDocument?.[0]]
                                        : undefined
                                }
                                entityName={entityName}
                                isDownloadDisabled={!isDownloadAllowed}
                            />,
                        },
                        {
                            title: 'Подпись xml:',
                            description: <SignatureItem
                                signatureName={reclaimingInfo.solutionDocument?.[0]?.signatureFileName}
                                attachmentId={reclaimingInfo.solutionDocument?.[0]?.attachmentId}
                                entityName={entityName}
                                isDownloadDisabled={!isDownloadAllowed}
                            />,
                        },
                        {
                            title: 'Файл решения в pdf:',
                            description: <DownloadDocumentListItem
                                fileList={
                                    reclaimingInfo?.solutionDocument?.[1]
                                        ? [reclaimingInfo.solutionDocument?.[1]]
                                        : undefined
                                }
                                entityName={entityName}
                                isDownloadDisabled={!isDownloadAllowed}
                            />,
                        },
                        {
                            title: 'Подпись pdf:',
                            description: <SignatureItem
                                signatureName={reclaimingInfo.solutionDocument?.[1]?.signatureFileName}
                                attachmentId={reclaimingInfo.solutionDocument?.[1]?.attachmentId}
                                entityName={entityName}
                                isDownloadDisabled={!isDownloadAllowed}
                            />,
                        },
                        {
                            title: 'Дата получения:',
                            description: <DateListItem
                                date={reclaimingInfo?.solutionAudit?.createdDate}
                            />,
                        },
                        {
                            title: 'Дата предоставления документов:',
                            description: <DateListItem
                                date={reclaimingInfo?.dateFromDecision}
                                dateFormat={DATE_DMY}
                            />,
                        },
                        ],
                    },
                ]}
            />
        </Spin>
    );
};
